import React, { useState, useEffect } from 'react';
import './UserPage.css';

const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

function UserPage() {
  const [featureText, setFeatureText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [pastEntries, setPastEntries] = useState([]);
  const [leafFeatures, setLeafFeatures] = useState([]);
  const [userVotes, setUserVotes] = useState(new Set());
  const [votingFeature, setVotingFeature] = useState(null);
  const [activeTab, setActiveTab] = useState('vote');
  const [commentModal, setCommentModal] = useState({ open: false, feature: null });
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [userName, setUserName] = useState('');
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [pendingComment, setPendingComment] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const isAdmin = !!localStorage.getItem('adminToken');

  useEffect(() => {
    loadPastEntries();
    loadLeafFeatures();
    loadUserVotes();
    loadUserName();
  }, []);

  const loadUserName = () => {
    const savedName = localStorage.getItem('mdm_user_name');
    if (savedName) {
      setUserName(savedName);
    }
  };

  const getSessionId = () => {
    let sessionId = localStorage.getItem('mdm_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('mdm_session_id', sessionId);
    }
    return sessionId;
  };

  const loadPastEntries = async () => {
    try {
      const sessionId = getSessionId();
      const response = await fetch(`${API_BASE}/api/user-submissions`, {
        headers: {
          'X-Session-Id': sessionId
        }
      });
      const submissions = await response.json();
      setPastEntries(submissions);
    } catch (error) {
      console.error('Error loading past entries:', error);
    }
  };

  const loadLeafFeatures = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/leaf-features`);
      const features = await response.json();
      setLeafFeatures(features);
    } catch (error) {
      console.error('Error loading leaf features:', error);
    }
  };

  const loadUserVotes = () => {
    try {
      const savedVotes = localStorage.getItem('mdm-user-votes');
      if (savedVotes) {
        setUserVotes(new Set(JSON.parse(savedVotes)));
      }
    } catch (error) {
      console.error('Error loading user votes:', error);
    }
  };

  const saveUserVotes = (votes) => {
    try {
      localStorage.setItem('mdm-user-votes', JSON.stringify([...votes]));
    } catch (error) {
      console.error('Error saving user votes:', error);
    }
  };

  const submitFeature = async (e) => {
    e.preventDefault();
    if (!featureText.trim()) return;

    setIsLoading(true);
    setMessage('');

    try {
      const sessionId = getSessionId();
      const response = await fetch(`${API_BASE}/api/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId
        },
        body: JSON.stringify({ featureText }),
      });

      const result = await response.json();
      
      if (result.success) {
        setMessage(result.message);
        setFeatureText('');
        await loadPastEntries(); // Refresh past entries
        await loadLeafFeatures(); // Refresh features list
      } else {
        setMessage(result.error || 'Error submitting feature');
      }
    } catch (error) {
      console.error('Error submitting feature:', error);
      setMessage('Error submitting feature');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (featureText.trim() && !isLoading) {
        submitFeature(e);
      }
    }
  };

  const handleVote = async (featureId) => {
    if (votingFeature === featureId) return; // Prevent double-clicking
    
    setVotingFeature(featureId);
    
    const hasVoted = userVotes.has(featureId);
    const action = hasVoted ? 'downvote' : 'upvote';
    
    try {
      const response = await fetch(`${API_BASE}/api/features/${featureId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });

      const result = await response.json();
      
      if (result.success) {
        // Update local votes
        const newVotes = new Set(userVotes);
        if (hasVoted) {
          newVotes.delete(featureId);
        } else {
          newVotes.add(featureId);
        }
        setUserVotes(newVotes);
        saveUserVotes(newVotes);
        
        // Update feature votes in UI
        setLeafFeatures(prev => prev.map(feature => 
          feature.id === featureId 
            ? { ...feature, votes: result.votes }
            : feature
        ).sort((a, b) => {
          if (b.votes !== a.votes) return b.votes - a.votes;
          return a.title.localeCompare(b.title);
        }));
      }
    } catch (error) {
      console.error('Error voting on feature:', error);
    } finally {
      setVotingFeature(null);
    }
  };

  const openCommentModal = async (feature) => {
    setCommentModal({ open: true, feature });
    setNewComment('');
    
    // Load comments for this feature
    try {
      const response = await fetch(`${API_BASE}/api/features/${feature.id}/comments`);
      const featureComments = await response.json();
      setComments(featureComments);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const closeCommentModal = () => {
    setCommentModal({ open: false, feature: null });
    setComments([]);
    setNewComment('');
    setShowNamePrompt(false);
    setPendingComment('');
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    const commentText = newComment.trim();
    if (!commentText) return;
    
    if (!userName) {
      setPendingComment(commentText);
      setShowNamePrompt(true);
      return;
    }
    
    await submitComment(commentText);
  };

  const submitComment = async (commentText) => {
    try {
      const sessionId = getSessionId();
      const response = await fetch(`${API_BASE}/api/features/${commentModal.feature.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userName,
          commentText,
          sessionId
        })
      });
      
      const result = await response.json();
      if (result.success) {
        setComments(prev => [...prev, result.comment]);
        setNewComment('');
        setPendingComment('');
        // Refresh the leaf features to update comment count
        loadLeafFeatures();
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
    }
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    const nameInput = e.target.elements.userName.value.trim();
    if (nameInput) {
      const confirmed = window.confirm(`Set your name as "${nameInput}"? This will be used for all future comments.`);
      if (confirmed) {
        setUserName(nameInput);
        localStorage.setItem('mdm_user_name', nameInput);
        setShowNamePrompt(false);
        if (pendingComment) {
          await submitComment(pendingComment);
        }
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const organizeFeaturesByBreadcrumbs = () => {
    const tree = {};
    
    leafFeatures.forEach(feature => {
      // Get the breadcrumb path (excluding the feature itself, which is the last item)
      const breadcrumbPath = feature.breadcrumbs.slice(0, -1);
      
      if (breadcrumbPath.length === 0) {
        // Top-level features without parent categories
        if (!tree['_uncategorized']) {
          tree['_uncategorized'] = {
            title: 'Uncategorized',
            features: [],
            children: {},
            key: '_uncategorized',
            level: 0
          };
        }
        tree['_uncategorized'].features.push(feature);
      } else {
        // Build nested structure
        let currentLevel = tree;
        let currentPath = '';
        
        breadcrumbPath.forEach((crumb, index) => {
          const nodeKey = currentPath ? `${currentPath}/${crumb.title}` : crumb.title;
          currentPath = nodeKey;
          
          if (!currentLevel[nodeKey]) {
            currentLevel[nodeKey] = {
              title: crumb.title,
              features: [],
              children: {},
              key: nodeKey,
              level: index,
              breadcrumb: crumb
            };
          }
          
          // If this is the last breadcrumb in the path, add the feature here
          if (index === breadcrumbPath.length - 1) {
            currentLevel[nodeKey].features.push(feature);
          }
          
          currentLevel = currentLevel[nodeKey].children;
        });
      }
    });
    
    // Sort features within each node by votes
    const sortNode = (node) => {
      if (node.features) {
        node.features.sort((a, b) => {
          if (b.votes !== a.votes) return b.votes - a.votes;
          return a.title.localeCompare(b.title);
        });
      }
      
      Object.values(node.children || {}).forEach(sortNode);
    };
    
    Object.values(tree).forEach(sortNode);
    
    return tree;
  };

  const renderTreeNode = (node, depth) => {
    const hasChildren = Object.keys(node.children || {}).length > 0;
    const hasFeatures = node.features && node.features.length > 0;
    const totalFeatureCount = getTotalFeatureCount(node);
    
    return (
      <div key={node.key} className={`tree-node depth-${depth}`}>
        {(hasChildren || hasFeatures) && (
          <button
            className={`category-header ${expandedCategories[node.key] ? 'expanded' : ''}`}
            onClick={() => toggleCategory(node.key)}
          >
            <span className="category-icon">
              {expandedCategories[node.key] ? '▼' : '▶'}
            </span>
            <span className="category-name">{node.title}</span>
            <span className="feature-count">
              {totalFeatureCount} feature{totalFeatureCount !== 1 ? 's' : ''}
            </span>
          </button>
        )}
        
        {expandedCategories[node.key] && (
          <div className="tree-content">
            {/* Render child categories first */}
            {hasChildren && (
              <div className="child-categories">
                {Object.values(node.children).map(childNode => 
                  renderTreeNode(childNode, depth + 1)
                )}
              </div>
            )}
            
            {/* Render features at this level */}
            {hasFeatures && (
              <div className="category-features">
                {node.features.map(feature => (
                  <div key={feature.id} className="feature-card">
                    <div className="vote-section">
                      <button
                        className={`vote-btn ${userVotes.has(feature.id) ? 'voted' : ''} ${votingFeature === feature.id ? 'voting' : ''}`}
                        onClick={() => handleVote(feature.id)}
                        disabled={votingFeature === feature.id}
                        title={userVotes.has(feature.id) ? 'Remove vote' : 'Vote for this feature'}
                      >
                        <i className="fas fa-chevron-up vote-icon"></i>
                        <span className="vote-count">{feature.votes}</span>
                      </button>
                      <button
                        className="comment-btn"
                        onClick={() => openCommentModal(feature)}
                        title="View and add comments"
                      >
                        <i className="fas fa-comment comment-icon"></i>
                        <span className="comment-count">{feature.commentCount || 0}</span>
                      </button>
                    </div>
                    <div className="feature-content">
                      <div className="feature-title">{feature.title}</div>
                      {feature.description && (
                        <div className="feature-description">{feature.description}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const getTotalFeatureCount = (node) => {
    let count = node.features ? node.features.length : 0;
    
    if (node.children) {
      Object.values(node.children).forEach(child => {
        count += getTotalFeatureCount(child);
      });
    }
    
    return count;
  };

  const deleteComment = async (commentId) => {
    if (!isAdmin) return;
    if (!window.confirm('Delete this comment permanently?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setComments(prev => prev.filter(c => c.id !== commentId));
      } else {
        alert(result.error || 'Failed to delete comment');
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment');
    }
  };

  return (
    <div className="user-page">
      <div className="content-container">
        <form onSubmit={submitFeature} className="feature-form">
          <textarea
            value={featureText}
            onChange={(e) => setFeatureText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe a feature you'd like to have, something you hate about current systems, or vague feelings about what you want to be able to do..."
            disabled={isLoading}
            rows="4"
          />
          <button type="submit" disabled={isLoading || !featureText.trim()}>
            {isLoading ? 'Submitting...' : 'Submit'}
          </button>
        </form>

        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        {/* Tab Switcher */}
        <div className="tab-switcher">
          <button
            className={`tab-btn ${activeTab === 'vote' ? 'active' : ''}`}
            onClick={() => setActiveTab('vote')}
          >
            View All
          </button>
          <button
            className={`tab-btn ${activeTab === 'view' ? 'active' : ''}`}
            onClick={() => setActiveTab('view')}
          >
            View by Category
          </button>
          <button
            className={`tab-btn ${activeTab === 'submissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('submissions')}
          >
            My Submissions
          </button>
        </div>

        {/* Vote Tab Content */}
        {activeTab === 'vote' && leafFeatures.length > 0 && (
          <div className="features-voting">
            <h2>All Features - Vote for Your Priorities</h2>
            <div className="features-list">
              {leafFeatures.map(feature => (
                <div key={feature.id} className="feature-card">
                  <div className="vote-section">
                    <button
                      className={`vote-btn ${userVotes.has(feature.id) ? 'voted' : ''} ${votingFeature === feature.id ? 'voting' : ''}`}
                      onClick={() => handleVote(feature.id)}
                      disabled={votingFeature === feature.id}
                      title={userVotes.has(feature.id) ? 'Remove vote' : 'Vote for this feature'}
                    >
                      <i className="fas fa-chevron-up vote-icon"></i>
                      <span className="vote-count">{feature.votes}</span>
                    </button>
                    <button
                      className="comment-btn"
                      onClick={() => openCommentModal(feature)}
                      title="View and add comments"
                    >
                      <i className="fas fa-comment comment-icon"></i>
                      <span className="comment-count">{feature.commentCount || 0}</span>
                    </button>
                  </div>
                  <div className="feature-content">
                    <div className="breadcrumbs">
                      {feature.breadcrumbs.slice(0, -1).map((crumb, index) => (
                        <span key={index} className={`breadcrumb level-${crumb.level}`}>
                          {crumb.title}
                          {index < feature.breadcrumbs.slice(0, -1).length - 1 && <span className="separator">→</span>}
                        </span>
                      ))}
                    </div>
                    <div className="feature-title">{feature.title}</div>
                    {feature.description && (
                      <div className="feature-description">{feature.description}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vote Tab - Empty State */}
        {activeTab === 'vote' && leafFeatures.length === 0 && (
          <div className="empty-state">
            <p>No features have been submitted yet. Add your first feature above!</p>
          </div>
        )}

        {/* View Tab Content */}
        {activeTab === 'view' && leafFeatures.length > 0 && (
          <div className="features-view">
            <h2>All Features - Organized by Category</h2>
            <div className="categories-list">
              {Object.values(organizeFeaturesByBreadcrumbs()).map(node => 
                renderTreeNode(node, 0)
              )}
            </div>
          </div>
        )}

        {/* View Tab - Empty State */}
        {activeTab === 'view' && leafFeatures.length === 0 && (
          <div className="empty-state">
            <p>No features have been submitted yet. Add your first feature above!</p>
          </div>
        )}

        {/* My Submissions Tab Content */}
        {activeTab === 'submissions' && pastEntries.length > 0 && (
          <div className="past-entries">
            <h2>Your Past Entries</h2>
            <div className="entries-list">
              {pastEntries.map(entry => (
                <div key={entry.id} className="entry-item">
                  <p>"{entry.original_text}"</p>
                  <small>{new Date(entry.created_at).toLocaleDateString()}</small>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Submissions Tab - Empty State */}
        {activeTab === 'submissions' && pastEntries.length === 0 && (
          <div className="empty-state">
            <p>You haven't submitted any features yet.</p>
          </div>
        )}
      </div>

      {/* Comment Modal */}
      {commentModal.open && (
        <div className="modal-overlay" onClick={closeCommentModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Comments</h3>
              <button className="close-btn" onClick={closeCommentModal}>×</button>
            </div>
            
            <div className="modal-feature">
              <div className="breadcrumbs">
                {commentModal.feature.breadcrumbs.map((crumb, index) => (
                  <span key={index} className={`breadcrumb level-${crumb.level}`}>
                    {crumb.title}
                    {index < commentModal.feature.breadcrumbs.length - 1 && <span className="separator">→</span>}
                  </span>
                ))}
              </div>
              <div className="feature-title">{commentModal.feature.title}</div>
              {commentModal.feature.description && (
                <div className="feature-description">{commentModal.feature.description}</div>
              )}
            </div>

            <div className="comments-section">
              <div className="comments-list">
                {comments.length === 0 ? (
                  <p className="no-comments">No comments yet. Be the first to comment!</p>
                ) : (
                  comments.map(comment => (
                    <div key={comment.id} className="comment">
                      <div className="comment-header">
                        <strong className="comment-author">{comment.user_name}</strong>
                        <span className="comment-date">{formatDate(comment.created_at)}</span>
                        {isAdmin && (
                          <button className="delete-comment-btn" title="Delete comment" onClick={() => deleteComment(comment.id)}>×</button>
                        )}
                      </div>
                      <div className="comment-text">{comment.comment_text}</div>
                    </div>
                  ))
                )}
              </div>

              {showNamePrompt ? (
                <form onSubmit={handleNameSubmit} className="name-form">
                  <p>Please enter your name for commenting:</p>
                  <input
                    type="text"
                    name="userName"
                    placeholder="Your name"
                    maxLength="50"
                    required
                    autoFocus
                  />
                  <div className="name-form-buttons">
                    <button type="submit">Set Name</button>
                    <button type="button" onClick={() => setShowNamePrompt(false)}>Cancel</button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleCommentSubmit} className="comment-form">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    maxLength="500"
                    rows="3"
                    required
                  />
                  <div className="comment-form-footer">
                    <span className="char-count">{newComment.length}/500</span>
                    <button type="submit" disabled={!newComment.trim()}>
                      Post Comment
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserPage;
